import { XMarkdown } from '@ant-design/x-markdown';
import DOMPurify from 'dompurify';
import React from 'react';
import { isHtmlContent } from '../../../utils/markdownParser';

type Props = {
  content: string;
  className?: string;
};

const STYLE_CLASSES =
  '[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-2 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mb-1 [&_p]:mb-2 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-0.5 [&_a]:text-azure [&_a]:underline [&_blockquote]:border-l-3 [&_blockquote]:border-gray-medium [&_blockquote]:pl-4 [&_blockquote]:text-gray-dark [&_blockquote]:my-2 [&_hr]:my-4 [&_hr]:border-gray-medium [&_code]:bg-gray [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_strong]:font-bold [&_em]:italic';

/**
 * Renders email/message content.
 * Detects whether content is HTML or Markdown and renders accordingly.
 * HTML content is sanitized with DOMPurify to prevent XSS.
 * Markdown content is rendered via XMarkdown (backward compatibility with older emails).
 */
export const MarkdownRenderer: React.FC<Props> = ({ content, className = '' }) => {
  if (!content) {
    return <div className={className} />;
  }

  // HTML content: sanitize with DOMPurify and render directly
  if (isHtmlContent(content)) {
    const sanitized = DOMPurify.sanitize(content);
    return (
      <div
        className={`${className} ${STYLE_CLASSES}`}
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    );
  }

  // Markdown content (backward compatibility with older stored emails)
  return (
    <div className={`${className} ${STYLE_CLASSES}`}>
      <XMarkdown>{content}</XMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
